import React, { useState, useEffect } from 'react'

import _ from 'lodash';

import {
    useTableCount,
    useTableFilters,
    useTableSelectAllState,
    useTableSelectedRows,
    useTableColumns,
} from '../../../../../redux/selectors';
import { useTranslation } from 'react-i18next'
import { IconButton, TableHead, TableRow, Tooltip, Checkbox } from '@material-ui/core'
import { Tune as TuneIcon, Block as BlockIcon, Sync as SyncIcon } from '@material-ui/icons'

import { thousandSeparator } from '../../../../../utils/tools'
import classes from './TableHeader.module.css'
import ColumnDisplayDialog from '../../Dialogs/ColumnDisplayDialog'
import {
    CustomEmptyTableCell,
    CustomTableCell,
    CustomTableHeaderCell,
    CustomTableCellInput,
    CustomTableRow,
    CustomTableSortLabel,
    CustomTotalsTableCell,
    CustomTableRowSeparator
} from '../CustomTableComponents/CustomTableComponents'
import { TableHeaderTooltip } from '../../../../Tooltips/BPMTooltips'
import { run_filter_delay } from '../../../Properties'

const TableHeader = (props) => {
    const {
        TableObject,
        filtersDisplay,
        handleClearFilters,
        checkboxDisplay,
        preColumnDisplay,
        handleSelectAll,
        onRequestSort,
        refreshTable,
        refreshButtonDisplay,
        lastQueryResult,
    } = props;

    const { t } = useTranslation()

    const [columnHandlerDisplay, setColumnHandlerDisplay] = useState(false);
    const [timer, setTimer] = useState(null)

    const tableRowsLength = useTableCount(TableObject.tableName)
    const selectedRows = useTableSelectedRows(TableObject.tableName)
    const allRowsSelected = useTableSelectAllState(TableObject.tableName)

    const storedTableSettings = useTableFilters(TableObject.tableName)
    const [localFilters, setLocalFilters] = useState(storedTableSettings.filters)
    const orderDirection = storedTableSettings.orderDirection;
    const orderField = storedTableSettings.orderField;
    const currentTableColumns = useTableColumns(TableObject.tableName)

    const TableFieldsInTotals = TableObject.getTableFieldsInTotals()

    useEffect(() => {
        return () => {
            if (timer){
                clearTimeout(timer)
            }
        }
    }, [])

    useEffect(() => {
        checkHidenFilters();
    }, [currentTableColumns])

    const commitFilters = (filters) => {
        let newTableConfig = {...storedTableSettings}
        newTableConfig.filters = filters

        TableObject.setTableFiltersState(newTableConfig)
    }

    const toggleColumnHandler = () => {
        setColumnHandlerDisplay(!columnHandlerDisplay)
    }

    const handleSelectAllAction = () => {
        if (getCheckBoxIndeterminateState()) {
            handleSelectAll(false);
        } else {
            handleSelectAll(!allRowsSelected);
        }
    }

    const onChangeFilter = (event, showNull) => {
        if (timer){
            clearTimeout(timer)
        }

        let new_filters = {...localFilters}
        let value = event.target.value

        // If the input value is an object (e.g. from a custom control), store it as a JSON string
        if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value)
        }

        new_filters[event.target.name] = value

        // If it's an empty string (after trimming), remove the filter entry
        if (typeof value === 'string' && value.trim().length === 0) {
            delete new_filters[event.target.name]
        }

        setTimer(setTimeout(() => commitFilters(new_filters), run_filter_delay))
        setLocalFilters(new_filters);
    }

    const handleClearLocalFilters = () => {
        setLocalFilters({})
        handleClearFilters()
    }

    const createSortHandler = (event, fieldId, column) => {
        if (!column.hideSort) {
            onRequestSort(event, fieldId)
        } else {
            event.stopPropagation();
            event.preventDefault();
        }
    }

    const allowColumnDrawingInHeader =(column) => {
        if (!column.section) {
            return true;
        }

        return TableObject.fieldDescriptions.filter(col => (col.section && col.section.id === column.section.id))[0] === column;
    }

    const isSectionRowVisible = () => {
        return TableObject.fieldDescriptions.filter(col => (col.section && col.section.label)).length > 0;
    }

    const getSectionlength = (column) => {
        if (!column.section) {
            return 0;
        }

        return TableObject.fieldDescriptions.filter(col => (col.section && col.section.id === column.section.id)).length;
    }

    const getCheckBoxIndeterminateState = () => {
        return selectedRows.length !== 0 && selectedRows.length !== tableRowsLength
    }

    const checkHidenFilters = () => {
        let new_filters = {...localFilters};
        const notVisibleColumns = currentTableColumns.filter(f => (f.visibility));

        let changeFilters = false;
        notVisibleColumns.forEach(column => {
            if (new_filters.hasOwnProperty(column.id)) {
                delete new_filters[column.id];
                changeFilters=true;
            }
        });

        if (changeFilters) {
            commitFilters(new_filters)
            setLocalFilters(new_filters);
        }
    }

    const getTotalsCellComponent = (field) => {
        if (lastQueryResult === undefined || lastQueryResult === null) {
            return null
        } else {
            const totals_data = Object.values(lastQueryResult)[0]

            let value = field.totalsSettings.mapper ? _.get(totals_data, field.totalsSettings.mapper, field.totalsSettings.defaultValue) : field.totalsSettings.defaultValue
            if (TableObject.getTotalsRowConfig().hideEmptyValues && !value) {
                value = ''
            } else {
                value = typeof(value) === 'number' ?
                    `${field.totalsSettings.prefix || ''} ${thousandSeparator(value, field.totalsSettings.precision || 0)} ${field.totalsSettings.sufix ||''}` 
                    : field.totalsSettings.defaultValue
            }

            return (
                <span style={{ 
                    fontSize: 13, 
                    fontWeight: 400, 
                    backgroundColor: TableObject.getTotalsRowConfig().backgroundColor || "white", 
                    color: TableObject.getTotalsRowConfig().color || '#0000008a' 
                }}>
                    {value}
                </span>
            )
        }
    }

    const gteTotalsRowLabel = () => {
        if (TableObject.getTotalsRowConfig().label && /[a-zA-Z]/.test(TableObject.getTotalsRowConfig().label)) {
            return t(TableObject.getTotalsRowConfig().label)
        } else if (TableObject.getTotalsRowConfig().label) {
            return TableObject.getTotalsRowConfig().label
        } else {
            return ''
        }
    }

    // Compute dynamic width for the last actions column based on configured action buttons (support up to 3)
    const computeActionsWidth = () => {
        const actionsConfig = TableObject.getRowActionButtonsConfig()
        const count = Math.min(3, (actionsConfig && actionsConfig.length) ? actionsConfig.length : 1)
        const perButton = 44; // approx width per medium IconButton including minimal gap
        const minWidth = 50;
        return Math.max(minWidth, count * perButton)
    }
    const actionsColWidth = computeActionsWidth()

    return (
        <>
            <ColumnDisplayDialog
                open={columnHandlerDisplay}
                TableObject={TableObject}
                onClose={toggleColumnHandler}
            />
            <TableHead className={classes.StickyHeader} id="stickyTableHeader">
                {/* This row is used to show the totals */}
                {TableFieldsInTotals && TableFieldsInTotals.length > 0 ? (
                    <>
                        <CustomTableRow key='rowP'>
                            {preColumnDisplay && (TableObject.getfiltersDisplay(filtersDisplay) || checkboxDisplay) ?
                                <CustomTotalsTableCell key='DUMMY_CELL_A' style={{ width: 50 }} />
                            : null}
                            <CustomTotalsTableCell
                                key='DUMMY_CELL_X'
                                colSpan={TableObject.getVisibleFields().length - TableFieldsInTotals.length}
                                style={{ textAlign: 'center' }}
                            >
                                {gteTotalsRowLabel()}
                            </CustomTotalsTableCell>
                            {TableFieldsInTotals.map((field, index) =>
                                <CustomTotalsTableCell key={`totals_${index}`} style={{ backgroundColor: TableObject.getTotalsRowConfig().backgroundColor || 'white', textAlign: field.fieldAlign}}>
                                    {getTotalsCellComponent(field)}
                                </CustomTotalsTableCell>
                            )}
                            <CustomTotalsTableCell key='DUMMY_CELL_B' style={{ width: actionsColWidth, backgroundColor: TableObject.getTotalsRowConfig().backgroundColor || 'white' }} />
                        </CustomTableRow>
                        <CustomTableRowSeparator key='rowQ'>
                            {preColumnDisplay && (TableObject.getfiltersDisplay(filtersDisplay) || checkboxDisplay) ?
                                <CustomTotalsTableCell key='DUMMY_CELL_C' style={{ backgroundColor: '#f1f1f1', width: 50 }} />
                            : null}
                            <CustomTotalsTableCell key='DUMMY_CELL_D' colSpan={TableObject.getVisibleFields().length} style={{ backgroundColor: '#f1f1f1', width: 50 }} />
                            <CustomTotalsTableCell key='DUMMY_CELL_E' style={{ backgroundColor: '#f1f1f1', width: 30 }} />
                        </CustomTableRowSeparator>
                    </>
                ) : null}

                {/* This row is used to group the columns by section */}
                {isSectionRowVisible() ?
                <CustomTableRow key='rowA'>
                    {/* 'Column Setup' Button */}
                    {preColumnDisplay && (TableObject.getfiltersDisplay(filtersDisplay) || checkboxDisplay) ?
                    <CustomTableCell  key='DUMMY_CELL' style={{width: 50}} />
                    :null
                    }
                    {/* Column Headers */}
                    {TableObject.fieldDescriptions.filter(column => (column.getVisibility(currentTableColumns))).map(column => (
                        allowColumnDrawingInHeader(column) ?
                        <CustomTableHeaderCell colSpan={ getSectionlength(column) }
                            key={column.id}
                            style={{fontSize: 14, textAlign: column.fieldAlign}}
                        >
                            {column.section?.label ?
                                <span style={{fontSize: 16}}>{column.section.translateLabel ? TableObject.tableTranslation.translate(column.section.label, t) : column.section.label}</span>
                            :
                            null}
                        </CustomTableHeaderCell>
                        : null
                    ))}
                    {/* Actions Column Header */}
                    <CustomTableCell  key='EDIT_COLUMN' style={{fontSize: 16, width: actionsColWidth, textAlign: 'center'}} />
                </CustomTableRow>
                :null
                }

                {/* First Header Row: This row is used to show the column headers */}
                <CustomTableRow key='rowB'>
                    {/* 'Column Setup' Button */}
                    {preColumnDisplay && (TableObject.getfiltersDisplay(filtersDisplay) || checkboxDisplay) ?
                        <CustomEmptyTableCell key='setupSection'>
                            <div style={{display: 'flex'}}>
                            {TableObject.getfiltersDisplay(filtersDisplay) ?
                                <Tooltip  key='ColumnSetup' title={t('Column Setup')}>
                                    <IconButton onClick={toggleColumnHandler} style={{padding: '0px 0px 0px 0px', margin: 0, color: 'white'}}>
                                        <TuneIcon style={{fontSize: 20}} />
                                    </IconButton>
                                </Tooltip>
                            : null}
                            {checkboxDisplay ?
                                <Tooltip  key='SelectAll' title={t('Select all rows')}>
                                    <Checkbox
                                        checked={allRowsSelected}
                                        indeterminate={getCheckBoxIndeterminateState()}
                                        style={{padding: '0px 0px 0px 5px', margin: 0, fontSize: 20, color: 'white'}}
                                        onClick={event => {
                                            event.stopPropagation()
                                            handleSelectAllAction()}
                                        }
                                    />
                                </Tooltip>
                            : null}
                            </div>
                        </CustomEmptyTableCell>
                        :
                        null
                    }
                    {/* Column Headers */}
                    {TableObject.fieldDescriptions.filter(column => (column.getVisibility(currentTableColumns))).map(column => (
                        <CustomTableHeaderCell
                            key={column.id}
                            sortDirection={orderField === column.orderIdentifier ? orderDirection : false}
                            style={{fontSize: 12, textAlign: column.fieldAlign}}
                        >
                            <TableHeaderTooltip title={!column.hideSort ? t('Sort') : column.getLabel(TableObject.tableTranslation, t)} placement='bottom-start' enterDelay={300}>
                                <CustomTableSortLabel
                                    active={orderField === column.orderIdentifier}
                                    hideSortIcon={column.hideSort}
                                    direction={orderDirection}
                                    onClick={(event) => createSortHandler(event, column.id, column)}
                                >
                                    {column.getLabel(TableObject.tableTranslation, t)}
                                </CustomTableSortLabel>
                            </TableHeaderTooltip>
                        </CustomTableHeaderCell>
                    ))}
                    {/* Actions Column Header with refresh table button*/}
                    <CustomTableCell  key='EDIT_COLUMN' style={{width: actionsColWidth, textAlign: 'center'}}>
                        {refreshButtonDisplay ?
                        <Tooltip title={t('Refresh table')}>
                            <IconButton onClick={() => refreshTable(false)} style={{ padding: 0, margin: 0, color: 'white'}}>
                                <SyncIcon style={{fontSize: 20}} />
                            </IconButton>
                        </Tooltip>
                        : null}
                    </CustomTableCell>
                </CustomTableRow>

                {/* Second Header Row: This row is used to show the filters */}
                {TableObject.getfiltersDisplay(filtersDisplay) &&
                    <TableRow>
                        {/* Checkboxes */}
                        {(preColumnDisplay || checkboxDisplay) ?
                            <CustomTableCellInput padding='checkbox' style={{width: 30, textAlign: 'center'}} />
                        : null}
                        {/* Column Headers */}
                        {/* Filters */}
                        {TableObject.getFilters({
                            onChange : ((event, showNull) => onChangeFilter(event, showNull)),
                            filters : localFilters || {},
                        })}
                        {/* Actions Column */}
                        <CustomTableCellInput key='FILTER_BUTTON' style={{width: actionsColWidth, textAlign: 'center'}}>
                            <Tooltip title={t('Clear Filters')}>
                                <IconButton onClick={handleClearLocalFilters} style={{padding: 0, margin: 0, color: 'white'}}>
                                    <BlockIcon style={{fontSize: 18}} />
                                </IconButton>
                            </Tooltip>
                        </CustomTableCellInput>
                    </TableRow>
                }
            </TableHead>
        </>
    )
}

export default TableHeader
